import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import './PageNav.css';
import { getAuth } from 'firebase/auth';
import { Link } from 'react-router-dom';
import DomainModal from './DomainModal';
import { getDatabase, ref as dbRef, get, onValue, set } from 'firebase/database';
import GlobalSettingsModal from './GlobalSettingsModal';
import PublishConfirmationModal from './PublishConfirmationModal';
import PublishSuccessToast from './PublishSuccessToast';
import { 
    publishSiteToFirestore, 
    getSiteInfo, 
    getSiteStats,
    firestorePublisherUtils 
} from '../utils/firestorePublisher';
import VersionManager from './VersionManager';

function PageNav({ projectName, pages: propPages, onGlobalSettingsToggle, onUndo, onRedo, canUndo = false, canRedo = false }) {
    const location = useLocation();
    const navigate = useNavigate();
    // Extract currentPageId from URL path
    const getCurrentPageId = () => {
        const pathParts = location.pathname.split('/').filter(Boolean);
        
        if (pathParts[0] === 'settings' && pathParts[1] === 'page') {
            // Extract page ID from settings route
            if (pathParts.length === 5) {
                // 3-level: /settings/page/userId/spaceId/pageName
                return `${pathParts[2]}/${pathParts[3]}/${pathParts[4]}`;
            } else if (pathParts.length === 4) {
                // 2-level: /settings/page/userId/pageName
                return `${pathParts[2]}/${pathParts[3]}`;
            } else if (pathParts.length === 3) {
                // Legacy: /settings/page/pageId
                return pathParts[2];
            }
        }
        
        return null;
    };
    
    const currentPageId = getCurrentPageId();
    const [previousPageId, setPreviousPageId] = useState(null);
    const [isMenuVisible, setIsMenuVisible] = useState(false);
    const menuRef = useRef();
    const [isActive, setIsActive] = useState(false);
    const db = getDatabase();
    const auth = getAuth();
    const currentUser = auth.currentUser;
    const [pages, setPages] = useState([]);
    
    // Use pages from props if provided, otherwise load them
    const effectivePages = propPages || pages;
    const [isDomainModalOpen, setIsDomainModalOpen] = useState(false);
    const [customDomainUrl, setCustomDomainUrl] = useState("");
    const currentPage = effectivePages.find(p => p.id === currentPageId);
    const livePageButtonClass = (customDomainUrl && isActive) ? "live-preview-button" : "live-preview-button inactive";
    
    // Check if staging has changes compared to live version
    const [hasChanges, setHasChanges] = useState(false);
    const [isGlobalSettingsModalOpen, setIsGlobalSettingsModalOpen] = useState(false);
    
    // Publish Confirmation Modal States
    const [showPublishModal, setShowPublishModal] = useState(false);
    const [publishModalType, setPublishModalType] = useState('publish');
    
    // Success Toast States
    const [showSuccessToast, setShowSuccessToast] = useState(false);
    const [successToastType, setSuccessToastType] = useState('publish');
    
    // Firestore Publishing States
    const [isFirestoreEnabled, setIsFirestoreEnabled] = useState(true); // Feature flag
    const [firestoreStats, setFirestoreStats] = useState(null);
    const [isPublishingToFirestore, setIsPublishingToFirestore] = useState(false);
    const [showVersionManager, setShowVersionManager] = useState(false);

    const handleDomainModalOpen = () => {
        setIsDomainModalOpen(true);
    };

    const handleDomainModalClose = () => {
        setIsDomainModalOpen(false);
    };

    const handleGlobalSettingsModalOpen = () => {
        setIsGlobalSettingsModalOpen(true);
        if (onGlobalSettingsToggle) {
            onGlobalSettingsToggle(true);
        }
    };

    const handleGlobalSettingsModalClose = () => {
        setIsGlobalSettingsModalOpen(false);
        if (onGlobalSettingsToggle) {
            onGlobalSettingsToggle(false);
        }
    };

    const handleDomainSubmit = (domainName) => {
        if (!validateCustomDomain(domainName)) {
            return;
        }

        const saveDomainName = (domainName) => {
            if (currentUser && effectivePages.length > 0) {
                const currentPage = effectivePages.find(p => p.id === currentPageId);
                if (currentPage) {
                    const currentPageRef = dbRef(db, `/pages/${currentPageId}`);
                    const publicPageRef = dbRef(db, `/publicPages/${domainName}`);

                    // Speichere den Domainnamen in /publicPages
                    set(publicPageRef, { pageId: currentPageId, userId: currentUser.uid })
                        .then(() => {
                            // Aktualisiere den Domainnamen in /pages
                            return set(currentPageRef, { ...currentPage, hasDomain: true, customDomain: domainName });
                        })
                        .then(() => {
                            console.log("Domainname erfolgreich gespeichert");
                            setIsDomainModalOpen(false);
                        })
                        .catch(error => {
                            console.error("Fehler beim Speichern des Domainnamens:", error);
                        });
                }
            }
        };

        const publicPageRef = dbRef(db, `/publicPages/${domainName}`);
        get(publicPageRef).then((snapshot) => {
            if (snapshot.exists()) {
                alert("Name existiert bereits. Bitte anderen Namen eingeben.");
            } else {
                saveDomainName(domainName);
            }
        }).catch((error) => {
            console.error("Fehler beim Überprüfen des Domainnamens:", error);
        });
    };

    const publishToLive = async () => {
        const currentPage = effectivePages.find(p => p.id === currentPageId);
        if (!currentPage) {
            console.error('❌ PageNav: No current page found for publishing', { 
                currentPageId, 
                availablePages: effectivePages.map(p => p.id),
                effectivePagesLength: effectivePages.length 
            });
            return;
        }

        console.log('🚀 PageNav: Publishing page to live:', currentPageId);
        
        try {
            // PARALLEL PUBLISHING: RTDB + Firestore
            const publishPromises = [];
            
            // 1. Traditional RTDB Publishing (existing system)
            publishPromises.push(publishToRTDB(currentPage));
            
            // 2. NEW: Firestore Publishing (if enabled)
            if (isFirestoreEnabled) {
                publishPromises.push(publishToFirestoreSystem(currentPage));
            }
            
            // Execute both publishing systems in parallel
            const results = await Promise.allSettled(publishPromises);
            
            // Check results
            const rtdbResult = results[0];
            const firestoreResult = results[1];
            
            if (rtdbResult.status === 'fulfilled') {
                console.log('✅ RTDB publishing successful');
                setIsActive(true);
            } else {
                console.error('❌ RTDB publishing failed:', rtdbResult.reason);
            }
            
            if (isFirestoreEnabled && firestoreResult) {
                if (firestoreResult.status === 'fulfilled') {
                    console.log('✅ Firestore publishing successful:', firestoreResult.value);
                    // Update Firestore stats (temporarily disabled)
                    // await loadFirestoreStats();
                } else {
                    console.error('❌ Firestore publishing failed:', firestoreResult.reason);
                }
            }
            
            // Show success toast (if at least RTDB succeeded)
            if (rtdbResult.status === 'fulfilled') {
                setSuccessToastType(hasChanges ? 'deploy' : 'publish');
                setShowSuccessToast(true);
            } else {
                alert('Error publishing page. Please try again.');
            }
            
        } catch (error) {
            console.error('❌ PageNav: Error in publishing workflow:', error);
            alert('Error publishing page. Please try again.');
        }
    };

    // Traditional RTDB Publishing (extracted from original publishToLive)
    const publishToRTDB = async (currentPage) => {
        // Get current page data from staging
        const stagingPageRef = dbRef(db, `/pages/${currentPageId}`);
        const stagingSnapshot = await get(stagingPageRef);
        
        if (!stagingSnapshot.exists()) {
            throw new Error('Staging page not found');
        }
        
        const stagingData = stagingSnapshot.val();
        
        // Prepare live page data with publication metadata
        const publishTimestamp = new Date().toISOString();
        const livePageData = {
            ...stagingData,
            isActive: true,
            publishedAt: publishTimestamp,
            updatedAt: publishTimestamp, // Sync timestamps for change detection
            lastPublishedBy: currentUser?.uid,
            publishVersion: Date.now() // Simple versioning
        };
        
        // Copy to live pages collection
        const livePageRef = dbRef(db, `/livePages/${currentPageId}`);
        await set(livePageRef, livePageData);
        
        // Update staging page with published status
        const updatedStagingData = {
            ...stagingData,
            isPublished: true,
            lastPublishedAt: new Date().toISOString(),
            publishVersion: Date.now()
        };
        
        await set(stagingPageRef, updatedStagingData);
        
        return { success: true, system: 'rtdb' };
    };

    // NEW: Firestore Publishing System
    const publishToFirestoreSystem = async (currentPage) => {
        setIsPublishingToFirestore(true);
        
        try {
            // Extract siteId from pageId
            const siteId = firestorePublisherUtils.pageIdToSiteId(currentPageId);
            
            // Generate version note
            const versionNote = firestorePublisherUtils.generateVersionNote(currentPage, hasChanges);
            
            // Publish to Firestore
            const result = await publishSiteToFirestore(siteId, currentPageId, versionNote);
            
            console.log('✅ Firestore publishing completed:', result);
            return result;
            
        } finally {
            setIsPublishingToFirestore(false);
        }
    };

    const unpublishFromLive = async () => {
        const currentPage = effectivePages.find(p => p.id === currentPageId);
        if (!currentPage) {
            console.error('❌ PageNav: No current page found for unpublishing', { 
                currentPageId, 
                availablePages: effectivePages.map(p => p.id) 
            });
            return;
        }

        console.log('📴 PageNav: Unpublishing page from live:', currentPageId);
        
        try {
            // Remove from live pages
            const livePageRef = dbRef(db, `/livePages/${currentPageId}`);
            await set(livePageRef, null);
            
            // Update staging page status
            const stagingPageRef = dbRef(db, `/pages/${currentPageId}`);
            const stagingSnapshot = await get(stagingPageRef);
            
            if (stagingSnapshot.exists()) {
                const stagingData = stagingSnapshot.val();
                const updatedStagingData = {
                    ...stagingData,
                    isPublished: false,
                    unpublishedAt: new Date().toISOString()
                };
                
                await set(stagingPageRef, updatedStagingData);
            }
            
            console.log('✅ PageNav: Page unpublished successfully');
            
            // Update local state
            setIsActive(false);
            
            // Show success toast
            setSuccessToastType('unpublish');
            setShowSuccessToast(true);
            
        } catch (error) {
            console.error('❌ PageNav: Error unpublishing page:', error);
            alert('Error unpublishing page. Please try again.');
        }
    };

    // REMOVED: toggleDraftMode - now handled directly in the toggle onChange

    // REMOVED: toggleActiveStatus - now using explicit publish/unpublish buttons

    const checkDomainBeforeGoingLive = async (pageId) => {
        const page = effectivePages.find(p => p.id === pageId);
        
        // New space system: Always has URL - can publish directly
        if (page && page.username) {
            console.log('✅ PageNav: New space system - publishing directly');
            await publishToLive();
            return;
        }
        
        // Legacy system: Check domain
        if (!page.hasDomain) {
            // Öffne das Modal für die Eingabe des Domainnamens
            setIsDomainModalOpen(true);
            return;
        }
        // Setze die Seite live, wenn das `hasDomain`-Feld gesetzt ist
        await publishToLive();
    };

    const validateCustomDomain = (domainName) => {
        const isValid = /^[a-zA-Z0-9-_]+$/.test(domainName);
        if (!isValid) {
            alert("Ungültiger Domainname. Nur Buchstaben, Zahlen, Bindestriche und Unterstriche sind erlaubt.");
        }
        return isValid;
    };

    const handleMouseEnter = () => {
        setIsMenuVisible(true);
    };

    // Function to get the appropriate navigation path based on current page
    const getNavigationPath = () => {
        if (!currentPageId) {
            return '/pages'; // Fallback to pages overview
        }

        const pathParts = currentPageId.split('/');
        
        if (pathParts.length === 3) {
            // 3-level structure: userId/spaceId/pageName -> navigate to space
            const spaceId = pathParts[1];
            return `/space/${spaceId}`;
        } else if (pathParts.length === 2) {
            // 2-level structure: userId/pageName -> navigate to pages overview
            return '/pages';
        } else {
            // Legacy structure -> navigate to pages overview
            return '/pages';
        }
    };

    // Function to get the appropriate back button text
    const getBackButtonText = () => {
        if (!currentPageId) {
            return 'BACK TO PAGES';
        }

        const pathParts = currentPageId.split('/');
        
        if (pathParts.length === 3) {
            // 3-level structure: userId/spaceId/pageName -> back to space
            return 'BACK TO SPACE';
        } else {
            // 2-level or legacy structure -> back to pages
            return 'BACK TO PAGES';
        }
    };

    const livePageUrl = `/page/${currentPageId}`;

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setIsMenuVisible(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [menuRef]);

    useEffect(() => {
        setPreviousPageId(currentPageId);
    }, [currentPageId]);

    // Eine Funktion, die die Sortierung der Seiten übernimmt
    const getSortedPages = () => {
        // Wenn effectivePages nicht definiert ist oder leer ist, gib ein leeres Array zurück
        if (!effectivePages || effectivePages.length === 0) {
            return [];
        }
    
        return effectivePages.sort((a, b) => {
            if (a.id === currentPageId) return -1;
            if (b.id === currentPageId) return 1;
            return 0;
        });
    };

    const handlePageClick = (pageId) => {
        console.log('🔍 PageNav: handlePageClick called with pageId:', pageId);
        
        // Check if pageId has the correct structure
        if (pageId && pageId.includes('/')) {
            const pathParts = pageId.split('/');
            if (pathParts.length === 3) {
                // 3-level: userId/spaceId/pageName
                navigate(`/settings/page/${pathParts[0]}/${pathParts[1]}/${pathParts[2]}`);
            } else if (pathParts.length === 2) {
                // 2-level: userId/pageName
                navigate(`/settings/page/${pathParts[0]}/${pathParts[1]}`);
            } else {
                // Fallback
                navigate(`/settings/page/${pageId}`);
            }
        } else {
            // Legacy structure
            navigate(`/settings/page/${pageId}`);
        }
    };

    // Prevent jumping during status checks
    const isCheckingStatusRef = useRef(false);
    
    // Track if we've already initialized the status for this page
    const initializedPageRef = useRef(null);
    
    useEffect(() => {
        if (!effectivePages || effectivePages.length === 0) {
            return; // Beenden, wenn keine Seiten vorhanden sind
        }
        const currentPage = effectivePages.find(p => p.id === currentPageId);
        if (currentPage && !isCheckingStatusRef.current) {
            // Only set initial state if this is a completely new page
            if (initializedPageRef.current !== currentPageId) {
                console.log('🔄 PageNav: Initializing status for new page:', currentPageId, 'with isActive:', currentPage.isActive);
                setIsActive(currentPage.isActive || false);
                initializedPageRef.current = currentPageId;
            }
        }
    }, [effectivePages, currentPageId]);

    // Load Firestore stats for current site with improved error handling
    const loadFirestoreStats = async () => {
        if (!isFirestoreEnabled || !currentPageId) return;
        
        try {
            const siteId = firestorePublisherUtils.pageIdToSiteId(currentPageId);
            
            // Prüfe ob siteId gültig ist
            if (!siteId || siteId.length < 10) {
                console.log('⚠️ Invalid siteId for Firestore stats:', siteId);
                setFirestoreStats(null);
                return;
            }
            
            const stats = await getSiteStats(siteId);
            setFirestoreStats(stats);
            
            console.log('📊 Firestore stats loaded:', stats);
        } catch (error) {
            // Verbesserte Fehlerbehandlung - keine wiederholten Logs
            if (error.code === 'permission-denied') {
                console.warn('⚠️ Firestore permissions insufficient - stats disabled');
            } else if (error.code === 'not-found') {
                console.log('ℹ️ Site not found in Firestore - using RTDB fallback');
            } else {
                console.error('❌ Error loading Firestore stats:', error.message);
            }
            setFirestoreStats(null);
        }
    };

    // Track the last time we checked status to avoid over-checking
    const lastStatusCheckRef = useRef(0);
    
    useEffect(() => {
        console.log('🔍 PageNav: Updating live URL and status', { currentPageId, pages: effectivePages.length, propPages: propPages?.length });
        console.log('🔍 PageNav: effectivePages first 3 IDs:', effectivePages.slice(0, 3).map(p => p.id));
        console.log('🔍 PageNav: Looking for currentPageId:', currentPageId);
        
        // 🔍 DEBUG: Search for hotelpalma pages
        const hotelpalmaPages = effectivePages.filter(p => p.id && p.id.includes('hotelpalma'));
        console.log('🔍 PageNav: Found hotelpalma pages:', hotelpalmaPages.length);
        if (hotelpalmaPages.length > 0) {
            console.log('🔍 PageNav: hotelpalma page IDs:', hotelpalmaPages.map(p => p.id));
        }
        
        const currentPage = effectivePages.find(p => p.id === currentPageId);
        console.log('🔍 PageNav: Current page found:', currentPage);
        
        // 🐛 DEBUG: Check if page exists with different matching
        if (!currentPage && effectivePages.length > 0) {
            const pageMatch = effectivePages.find(p => {
                const matches = p.id === currentPageId || 
                               p.slug === currentPageId || 
                               p.pageName === currentPageId;
                if (matches) {
                    console.log('🔍 PageNav: FOUND page with different key!', { pageId: p.id, slug: p.slug, pageName: p.pageName, currentPageId });
                }
                return matches;
            });
            if (pageMatch) {
                console.log('✅ PageNav: Found page using alternative matching!', pageMatch);
            }
        }
        
        if (currentPage) {
            // Check if page is published and if there are changes
            const checkPublishStatus = async () => {
                const now = Date.now();
                // Only check if it's been more than 2 seconds since last check
                if (now - lastStatusCheckRef.current < 2000) {
                    console.log('⏱️ PageNav: Skipping status check (too recent)');
                    return;
                }
                
                lastStatusCheckRef.current = now;
                isCheckingStatusRef.current = true; // Prevent jumping during check
                
                try {
                    const livePageRef = dbRef(db, `/livePages/${currentPageId}`);
                    const livePageSnapshot = await get(livePageRef);
                    const isPublished = livePageSnapshot.exists();
                    
                    // Debounced update: Only update if status actually changed AND we're confident about the result
                    setIsActive(prevIsActive => {
                        if (prevIsActive !== isPublished) {
                            console.log('🔄 PageNav: Firebase status differs from local:', { 
                                local: prevIsActive, 
                                firebase: isPublished, 
                                pageId: currentPageId 
                            });
                            // Only update if we're confident this is the authoritative source
                            return isPublished;
                        }
                        console.log('✅ PageNav: Status consistent:', { status: prevIsActive, pageId: currentPageId });
                        return prevIsActive;
                    });
                    
                    if (isPublished && currentPage) {
                        // Compare staging vs live to detect changes
                        const livePageData = livePageSnapshot.val();
                        const stagingPageRef = dbRef(db, `/pages/${currentPageId}`);
                        const stagingPageSnapshot = await get(stagingPageRef);
                        
                        if (stagingPageSnapshot.exists()) {
                            const stagingPageData = stagingPageSnapshot.val();
                            
                            // Simple change detection: compare updatedAt timestamps
                            const liveUpdatedAt = new Date(livePageData.updatedAt || livePageData.publishedAt || 0);
                            const stagingUpdatedAt = new Date(stagingPageData.updatedAt || 0);
                            
                            const hasNewChanges = stagingUpdatedAt > liveUpdatedAt;
                            
                            console.log('🔍 PageNav: Change detection:', { 
                                currentPageId, 
                                isPublished, 
                                hasNewChanges,
                                liveUpdatedAt: liveUpdatedAt.toISOString(),
                                stagingUpdatedAt: stagingUpdatedAt.toISOString()
                            });
                            
                            setHasChanges(hasNewChanges);
                        } else {
                            setHasChanges(false);
                        }
                    } else {
                        setHasChanges(false);
                    }
                    
                } catch (error) {
                    console.error('❌ PageNav: Error checking publish status:', error);
                    // Fallback: Only update if we don't have a valid status yet
                    setIsActive(prevIsActive => {
                        const fallbackStatus = currentPage?.isActive || false;
                        if (prevIsActive === undefined || prevIsActive === null) {
                            return fallbackStatus;
                        }
                        return prevIsActive;
                    });
                    setHasChanges(false);
                } finally {
                    isCheckingStatusRef.current = false; // Allow updates again
                }
            };
            
            // Debounce the status check to prevent rapid updates
            const timeoutId = setTimeout(() => {
                checkPublishStatus();
            }, 1200); // Increased timeout for less aggressive checking
            
            // Load Firestore stats if enabled (temporarily disabled due to permission issues)
            if (isFirestoreEnabled && false) { // Temporarily disabled
                loadFirestoreStats();
            }
            
            // Generate correct live URL based on page structure (regardless of active status for preview)
            if (currentPage.username) {
                // New space system: bonusclub.link/to/username/slug (with /to/ prefix for consistency)
                const liveUrl = currentPage.isHomePage || currentPage.slug === "/" ? 
                    `/to/${currentPage.username}/` :                    // Home page: /to/username/
                    `/to/${currentPage.username}/${currentPage.slug}`;  // Sub page: /to/username/slug
                setCustomDomainUrl(liveUrl);
                console.log('✅ PageNav: New space system URL (with /to/):', liveUrl, { isHomePage: currentPage.isHomePage, slug: currentPage.slug });
            } else if (currentPage.customDomain) {
                // Legacy system: bonusclub.link/to/customDomain
                const liveUrl = `/to/${currentPage.customDomain}`;
                setCustomDomainUrl(liveUrl);
                console.log('✅ PageNav: Legacy system URL:', liveUrl);
            } else {
                setCustomDomainUrl("");
                console.log('⚠️ PageNav: No URL pattern matched', currentPage);
            }
            
            return () => clearTimeout(timeoutId);
        } else {
            setCustomDomainUrl("");
            // Only set isActive to false if we're not currently checking status
            if (!isCheckingStatusRef.current) {
                setIsActive(false);
            }
            console.log('⚠️ PageNav: Page not found');
        }
    }, [effectivePages, currentPageId, db, isFirestoreEnabled]);

    // Fetch pages for the current user (only if not provided via props)
    useEffect(() => {
        if (currentUser && !propPages) {
            const userPagesRef = dbRef(db, `/pages/${currentUser.uid}`);
            onValue(userPagesRef, (snapshot) => {
                const userPagesData = snapshot.val() || {};
                const userPages = [];
                
                // Handle both 2-level and 3-level structures
                Object.keys(userPagesData).forEach(key => {
                    const pageOrSpace = userPagesData[key];
                    
                    if (pageOrSpace.widgets) {
                        // 2-level: Direct page
                        userPages.push({
                            id: `${currentUser.uid}/${key}`,
                            slug: key,
                            ...pageOrSpace
                        });
                    } else {
                        // 3-level: Space with pages
                        Object.keys(pageOrSpace).forEach(pageName => {
                            const pageData = pageOrSpace[pageName];
                            if (pageData.widgets) {
                                userPages.push({
                                    id: `${currentUser.uid}/${key}/${pageName}`,
                                    spaceId: key,
                                    slug: pageData.slug || pageName, // Use pageData.slug if available
                                    pageName: pageName, // Keep internal page name
                                    ...pageData
                                });
                            }
                        });
                    }
                });

                // Sortiere Pages nach Erstellungsdatum (neueste zuerst)
                const sortedPages = userPages.sort((a, b) => {
                    const dateA = new Date(a.createdAt || a.updatedAt || 0);
                    const dateB = new Date(b.createdAt || b.updatedAt || 0);
                    return dateB - dateA; // Neueste zuerst (descending)
                });
                
                setPages(sortedPages);
                console.log('📄 PageNav: Loaded pages from new structure (sorted newest first):', sortedPages);
            });
        }
    }, [currentUser, db, propPages]);

    return (
        <div className="page-builder-header">
            {isDomainModalOpen && (
                <DomainModal
                    onDomainSubmit={handleDomainSubmit}
                    onClose={handleDomainModalClose}
                />
            )}
            <div className="page-nav-menu-left">
                <NavLink to={getNavigationPath()} className="page-nav-menu-left">
                    <img src="/img/BonusClub_Logo.webp" alt="Cooler text" />
                </NavLink>
                <hr className="hrrrrr" />
                <div className="page-nav-text-item">
                    <div onClick={handleMouseEnter}>
                        <p>{projectName}</p>
                        <img src="/img/down.svg" alt="" />
                    </div>
                    <div className="page-nav-text-item" id="menu-item-1">
                        <button className="page-nav-text-item-button" style={{ backgroundColor: 'white', border: '1px solid lightgrey', display: 'flex', alignItems: 'center', gap: '8px' }} onClick={handleGlobalSettingsModalOpen}>
                           <img src="/img/settings.svg" alt="" style={{ width: '16px', height: '16px' }} />
                            Page Settings
                        </button>
                        {/* <button 
                            className="template-button"
                            onClick={() => navigate(`/settings/page/${currentPageId}/templates`)} // Beispiel für die Navigation
                        >
                            Templates
                        </button> */}

                        {isGlobalSettingsModalOpen && (
                            <GlobalSettingsModal
                                show={isGlobalSettingsModalOpen}
                                onClose={handleGlobalSettingsModalClose}
                                pageId={currentPageId}
                            />
                        )}
                    </div>
                    <div ref={menuRef}
                        className="hover-menu"
                        style={{ display: isMenuVisible ? 'block' : 'none' }}
                        onMouseLeave={() => setIsMenuVisible(false)}>
                        <div className="hover-menu-comp">
                            <div className="hover-menu-comp-top">
                                <p>Currently Open</p>
                                <Link target="_blank" to={getNavigationPath()} className="create-new-page-link" style={{ textDecoration: 'none' }}><h4>Create New Page</h4></Link>
                            </div>
                            {getSortedPages().map((page) => (
                                <div key={page.id}
                                    className={`hover-menu-comp-mid ${page.id === currentPageId ? 'current-page' : ''}`}
                                    onClick={() => handlePageClick(page.id)}>
                                    <div className="hover-menu-comp-mid-left">
                                        <img src="/img/landing-page.svg" alt="" />
                                    </div>
                                    <div className="hover-menu-comp-mid-middle">
                                        <h5>{page.name}</h5>
                                        <p>{Array.isArray(page.widgets) ? page.widgets.length : 0} <span>Widgets</span></p>
                                    </div>
                                    <div className="hover-menu-comp-mid-right">
                                        {page.id === currentPageId && <img src="/img/tick.svg" alt="" />}
                                    </div>
                                </div>
                            ))}
                            <div className="hover-menu-comp-bottom">
                                <Link target="_blank" to={getNavigationPath()} className="create-new-page-link" style={{ textDecoration: 'none', color: 'black' }}>{getBackButtonText()}</Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="menu-right">
                {/* 🔄 Undo/Redo Buttons */}
                <div className="undo-redo-buttons" style={{ 
                    display: 'flex', 
                    gap: '6px', 
                    marginRight: '15px',
                    alignItems: 'center'
                }}>
                    <button
                        className="undo-btn"
                        onClick={onUndo}
                        disabled={!canUndo}
                        title="Undo (Ctrl+Z)"
                        style={{
                            background: canUndo 
                                ? 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)'
                                : '#e5e7eb',
                            border: 'none',
                            borderRadius: '100px',
                            padding: '8px 14px',
                            cursor: canUndo ? 'pointer' : 'not-allowed',
                            opacity: canUndo ? 1 : 0.5,
                            color: canUndo ? '#fff' : '#9ca3af',
                            fontSize: '13px',
                            fontWeight: '600',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            boxShadow: canUndo 
                                ? '0 2px 6px rgba(99, 102, 241, 0.3)'
                                : '0 1px 3px rgba(0, 0, 0, 0.1)',
                            transform: canUndo ? 'translateY(0)' : 'none'
                        }}
                        onMouseEnter={(e) => {
                            if (canUndo) {
                                e.currentTarget.style.transform = 'translateY(-1px)';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(99, 102, 241, 0.4)';
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (canUndo) {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 2px 6px rgba(99, 102, 241, 0.3)';
                            }
                        }}
                    >
                        <i className="fas fa-undo" style={{ fontSize: '12px' }}></i>
                        <span style={{ fontSize: '11px', fontWeight: '600', letterSpacing: '0.3px' }}>Undo</span>
                    </button>
                    
                    <button
                        className="redo-btn"
                        onClick={onRedo}
                        disabled={!canRedo}
                        title="Redo (Ctrl+Y)"
                        style={{
                            background: canRedo 
                                ? 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)'
                                : '#e5e7eb',
                            border: 'none',
                            borderRadius: '100px',
                            padding: '8px 14px',
                            cursor: canRedo ? 'pointer' : 'not-allowed',
                            opacity: canRedo ? 1 : 0.5,
                            color: canRedo ? '#fff' : '#9ca3af',
                            fontSize: '13px',
                            fontWeight: '600',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            boxShadow: canRedo 
                                ? '0 2px 6px rgba(99, 102, 241, 0.3)'
                                : '0 1px 3px rgba(0, 0, 0, 0.1)',
                            transform: canRedo ? 'translateY(0)' : 'none'
                        }}
                        onMouseEnter={(e) => {
                            if (canRedo) {
                                e.currentTarget.style.transform = 'translateY(-1px)';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(99, 102, 241, 0.4)';
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (canRedo) {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 2px 6px rgba(99, 102, 241, 0.3)';
                            }
                        }}
                    >
                        <i className="fas fa-redo" style={{ fontSize: '12px' }}></i>
                        <span style={{ fontSize: '11px', fontWeight: '600', letterSpacing: '0.3px' }}>Redo</span>
                    </button>
                </div>

                {/* Publishing Status */}
                {isPublishingToFirestore && (
                    <span className="publishing-indicator" title="Publishing to Firestore...">
                        <i className="fas fa-spinner fa-spin" style={{ color: '#2196F3', marginRight: '4px' }}></i>
                        Publishing...
                    </span>
                )}

    
                
                {/* Draft/Live Toggle with integrated label */}
                <div className="switch-container-with-label">
                    <span className={`switch-label ${isActive ? 'live' : 'draft'}`}>
                        {isActive ? "Live" : "Draft"}
                    </span>
                    <label className="switch">
                        <input 
                            type="checkbox" 
                            checked={isActive} 
                            onChange={() => {
                                if (!isActive) {
                                    // Draft → Live: Show publish modal
                                    setPublishModalType('publish');
                                    setShowPublishModal(true);
                                } else {
                                    // Live → Draft: Show unpublish modal
                                    setPublishModalType('unpublish');
                                    setShowPublishModal(true);
                                }
                            }}
                            title={isActive ? "Switch to draft mode (page goes offline)" : "Publish to live (page goes online)"}
                            disabled={isPublishingToFirestore}
                        />
                        <span className="slider round"></span>
                    </label>
                </div>

                {/* Action Buttons Group */}
                <div className="action-buttons-group">
                    {/* Deploy Button - Show when Live + Has Changes */}
                    {isActive && hasChanges && (
                        <button 
                            className="deploy-btn-compact"
                            onClick={() => {
                                setPublishModalType('deploy');
                                setShowPublishModal(true);
                            }}
                            title="Deploy your latest changes to the live page"
                            disabled={isPublishingToFirestore}
                        >
                            <i className="fas fa-rocket"></i>
                            Launch
                        </button>
                    )}

                    {/* Version Manager Button - Show if Firestore has versions */}
                    {isFirestoreEnabled && firestoreStats?.totalVersions > 0 && (
                        <button
                            className="version-manager-btn"
                            onClick={() => setShowVersionManager(true)}
                            title={`View ${firestoreStats.totalVersions} version(s)`}
                            style={{ 
                                background: 'none',
                                borderRadius: '4px',
                                padding: '6px 8px',
                                cursor: 'pointer',
                                marginLeft: '5px',
                                marginRight: '5px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                            }}
                        >
                            <i className="fas fa-history" style={{ fontSize: '11px', color: '#666' }}></i>
                            <span style={{ fontSize: '10px', color: '#666' }}>{firestoreStats.totalVersions}</span>
                        </button>
                    )}

                    {/* Firestore Live URL - Show if Firestore is published */}
                 
                    
                    {/* Compact Live Page Link - Icon only */}
                    <a href={customDomainUrl || "#"}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`live-link-icon ${(customDomainUrl && isActive) ? 'active' : 'inactive'}`}
                        onClick={(e) => {
                            if (!customDomainUrl || !isActive) {
                                e.preventDefault();
                                if (!isActive) {
                                    alert('Please publish the page first to view it live.');
                                } else {
                                    console.log('⚠️ PageNav: No URL available for preview');
                                }
                            }
                        }}
                        title={isActive ? "View live page (RTDB)" : "Page not live"}
                    >
                        <img src="/img/link_1.svg" alt="" style={{ width: '14px', height: '14px', filter: 'invert(1)' }}/>
                    </a>
                </div>
            </div>
            
            {/* Publish Confirmation Modal */}
            <PublishConfirmationModal
                isOpen={showPublishModal}
                onClose={() => setShowPublishModal(false)}
                onConfirm={async () => {
                    try {
                        switch (publishModalType) {
                            case 'publish':
                                await publishToLive();
                                break;
                            case 'deploy':
                                await publishToLive();
                                break;
                            case 'unpublish':
                                await unpublishFromLive();
                                break;
                        }
                    } catch (error) {
                        console.error('❌ Modal action failed:', error);
                    }
                }}
                type={publishModalType}
                pageTitle={currentPage?.name || 'Your Page'}
                hasChanges={hasChanges}
            />
            
            {/* Success Toast */}
            <PublishSuccessToast
                isVisible={showSuccessToast}
                onClose={() => setShowSuccessToast(false)}
                type={successToastType}
                pageTitle={currentPage?.name || 'Your Page'}
                liveUrl={customDomainUrl ? `https://bonusclub.link${customDomainUrl}` : ''}
            />
            
            {/* Version Manager Modal */}
            {isFirestoreEnabled && (
                <VersionManager
                    siteId={currentPageId ? firestorePublisherUtils.pageIdToSiteId(currentPageId) : null}
                    isOpen={showVersionManager}
                    onClose={() => setShowVersionManager(false)}
                    onRollback={async (result) => {
                        // Reload Firestore stats after rollback (temporarily disabled)
                        // await loadFirestoreStats();
                        
                        // Show success toast
                        setSuccessToastType('rollback');
                        setShowSuccessToast(true);
                        
                        // Close version manager
                        setShowVersionManager(false);
                    }}
                />
            )}
        </div>
    );
}

export default PageNav;
